// File: src/pages/purchase/PurchaseReturn.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
} from "reactstrap";
import { FaFilter } from "react-icons/fa";
import { Eye } from "react-feather";
import { ImDownload } from "react-icons/im";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ---- your project imports (per instructions) ----
import UserContext from "../import/context/Context";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { Purchase_ReturnList } from "../import/ApiEndPoint/ApiCalling";

// ---------- tiny page-scoped CSS ----------
const injectOnce = () => {
  if (document.getElementById("pr-inline-css")) return;
  const style = document.createElement("style");
  style.id = "pr-inline-css";
  style.innerHTML = `
    .pr-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:900; padding:6px 10px; border-radius:999px;
    }
    .pr-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .pr-card-light{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; padding:10px 12px; }
    .pr-title{
      font-weight:900; text-transform:uppercase; font-size:18px; letter-spacing:.02em;
      display:flex; gap:10px; align-items:center; flex-wrap:wrap;
    }
    .pr-actionsbar{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
    .pr-search{ min-width:260px; }
    .pr-btn{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em; text-transform:uppercase;
    }
    .pr-btn-outline{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em; background:#fff;
    }

    .pr-filterbar{
      margin-top:10px;
      background:#fff;
      border:1px dashed #cbd5e1;
      border-radius:14px;
      padding:10px 12px;
    }
    .pr-filter-row{
      display:flex;
      gap:10px;
      flex-wrap:wrap;
      align-items:flex-end;
    }
    .pr-fitem{ flex:1 1 180px; min-width:180px; }
    .pr-fitem label{ font-size:12px; font-weight:900; color:#0f172a; letter-spacing:.02em; margin-bottom:4px; }
    .pr-fitem .form-control{ border-radius:12px; }
    .pr-chipline{ margin-top:10px; display:flex; flex-wrap:wrap; gap:8px; }
    .pr-chip{
      display:inline-flex; align-items:center; gap:8px;
      padding:6px 10px; border-radius:999px;
      background:#f1f5f9; border:1px solid #e5e7eb; color:#0f172a;
      font-weight:900; font-size:12px; letter-spacing:.02em;
    }

    .pr-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .pr-table{ width:100%; border-collapse:collapse; min-width:1080px; }
    .pr-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:900; text-transform:uppercase; font-size:12px; letter-spacing:.04em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .pr-table tbody td{ padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }
    .pr-table tbody tr:hover{ background:#f9fbff; }
    .pr-eye{ cursor:pointer; }

    .pick-box{
      border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px;
    }
    .pick-item{
      padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; background:#fff; cursor:pointer;
    }
  `;
  document.head.appendChild(style);
};

// helpers
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) ? n : d;
};
const fmtDate = (d) => (d ? String(d).split("T")[0] : "");
const getRowDate = (r) => {
  const d = r?.date || r?.createdAt || r?.created_at || r?.updatedAt;
  const t = d ? new Date(d) : null;
  return t && !isNaN(+t) ? t : null;
};

// ---- main page ----
export default function PurchaseReturn() {
  const navigate = useNavigate();
  const ctx = useContext(UserContext); // kept (some projects rely on this presence)
  useEffect(() => injectOnce(), []);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
  });

  const [masterShow, setMasterShow] = useState(false);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  // redesigned filters
  const [showFilters, setShowFilters] = useState(false);
  const [fSupplier, setFSupplier] = useState("");
  const [fInvoice, setFInvoice] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fMinReturn, setFMinReturn] = useState("");
  const [fMaxReturn, setFMaxReturn] = useState("");

  // column chooser
  const [modalCols, setModalCols] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  // ----- column definitions -----
  const baseColumns = useMemo(
    () => [
      {
        key: "uid",
        header: "UID",
        get: (_r, i) => i + 1,
      },
      {
        key: "_actions_",
        header: "ACTIONS",
        get: () => "",
        render: (r) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {permissions?.View && (
              <Eye
                size={16}
                color="green"
                className="pr-eye"
                onClick={() => {
                  setViewRow(r);
                  setViewOpen(true);
                }}
                title="View details"
              />
            )}
          </div>
        ),
      },
      {
        key: "sgstTotal",
        header: "RETURN SGST",
        get: (r) => toNum(r?.sgstTotal),
      },
      {
        key: "cgstTotal",
        header: "RETURN CGST",
        get: (r) => toNum(r?.cgstTotal),
      },
      {
        key: "igstTotal",
        header: "RETURN IGST",
        get: (r) => toNum(r?.igstTotal),
      },
      {
        key: "Return_amount",
        header: "RETURN AMOUNT",
        get: (r) => toNum(r?.Return_amount),
      },
      {
        key: "invoiceId",
        header: "INVOICE NO.",
        get: (r) => r?.orderId?.invoiceId || "-",
      },
      {
        key: "purchaseDate",
        header: "PURCHASE DATE",
        get: (r) => fmtDate(r?.orderId?.date),
      },
      {
        key: "supplier",
        header: "SUPPLIER NAME",
        get: (r) => r?.partyId?.CompanyName || "-",
      },
      {
        key: "pan",
        header: "COMPANY PAN NO",
        get: (r) => r?.partyId?.comPanNo || "-",
      },
      {
        key: "amount",
        header: "AMOUNT",
        get: (r) => (Number.isFinite(+r?.amount) ? (+r.amount).toFixed(2) : ""),
      },
      {
        key: "igst",
        header: "IGST",
        get: (r) => toNum(r?.igstTotal),
      },
      {
        key: "Tax",
        header: "TAX",
        get: (r) =>
          toNum(r?.igstTotal) > 0
            ? toNum(r?.igstTotal)
            : toNum(r?.sgstTotal) + toNum(r?.cgstTotal),
      },
      {
        key: "sgst",
        header: "SGST",
        get: (r) => toNum(r?.sgstTotal),
      },
      {
        key: "cgst",
        header: "CGST",
        get: (r) => toNum(r?.cgstTotal),
      },
      { key: "roundOff", header: "ROUND OFF", get: (r) => toNum(r?.roundOff) },
      // As in your original, Grand Total cell actually showed Return_amount
      {
        key: "grandTotal",
        header: "GRAND TOTAL",
        get: (r) => toNum(r?.Return_amount),
      },
    ],
    [permissions],
  );

  // column keys state (persisted)
  const [selectedKeys, setSelectedKeys] = useState([]);
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [baseColumns]);

  useEffect(() => {
    // keeping your existing storage key for this page
    const raw = localStorage.getItem("TargetList");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "string")
          next = parsed.filter((k) => colMap.has(k));
        else {
          const maybeKeys = parsed.map((c) => c?.key || c?.field || c);
          next = maybeKeys.filter((k) => colMap.has(k));
        }
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  // visible columns
  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  // ----- load data -----
  const reload = async (id, db) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await Purchase_ReturnList(id, db);
      const list = Array.isArray(res?.PurchaseReturn) ? res.PurchaseReturn : [];

      list.forEach((x) => {
        x.Tax =
          toNum(x?.igstTotal) > 0
            ? toNum(x?.igstTotal)
            : toNum(x?.cgstTotal) + toNum(x?.sgstTotal);
      });

      const final = list.slice().reverse();
      setRows(final);
      setAllRows(final);
    } catch (e) {
      console.error(e);
      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load purchase returns.",
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const inside = CheckPermission("Purchase Return");
      if (inside && typeof inside === "object")
        setPermissions((p) => ({ ...p, ...inside }));
      setMasterShow(user?.rolename?.roleName === "MASTER");

      await reload(user?._id, user?.database);
    })();
  }, []);

  // ----- filters pipeline (filters first, then search) -----
  const filteredByFilters = useMemo(() => {
    const supplierTerm = String(fSupplier || "")
      .trim()
      .toLowerCase();
    const invTerm = String(fInvoice || "")
      .trim()
      .toLowerCase();

    const from = fFrom ? new Date(`${fFrom}T00:00:00`) : null;
    const to = fTo ? new Date(`${fTo}T23:59:59`) : null;

    const minR = fMinReturn !== "" ? Number(fMinReturn) : null;
    const maxR = fMaxReturn !== "" ? Number(fMaxReturn) : null;

    return (rows || []).filter((r) => {
      if (supplierTerm) {
        const nm = String(r?.partyId?.CompanyName || "").toLowerCase();
        if (!nm.includes(supplierTerm)) return false;
      }

      if (invTerm) {
        const inv = String(r?.orderId?.invoiceId || "").toLowerCase();
        if (!inv.includes(invTerm)) return false;
      }

      const dt = getRowDate(r) || getRowDate(r?.orderId);
      if (from && dt && dt < from) return false;
      if (to && dt && dt > to) return false;

      const retAmt = toNum(r?.Return_amount, 0);
      if (minR !== null && Number.isFinite(minR)) {
        if (retAmt < minR) return false;
      }
      if (maxR !== null && Number.isFinite(maxR)) {
        if (retAmt > maxR) return false;
      }

      return true;
    });
  }, [rows, fSupplier, fInvoice, fFrom, fTo, fMinReturn, fMaxReturn]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByFilters;
    const searchCols = visibleCols.filter((c) => c.key !== "_actions_");
    return (filteredByFilters || []).filter((r, i) =>
      searchCols.some((c) => toStr(c.get(r, i)).toLowerCase().includes(term)),
    );
  }, [filteredByFilters, search, visibleCols]);

  const chips = useMemo(() => {
    const out = [];
    if (fSupplier.trim()) out.push({ k: "Supplier", v: fSupplier.trim() });
    if (fInvoice.trim()) out.push({ k: "Invoice", v: fInvoice.trim() });
    if (fFrom) out.push({ k: "From", v: fFrom });
    if (fTo) out.push({ k: "To", v: fTo });
    if (fMinReturn !== "") out.push({ k: "Min Return", v: fMinReturn });
    if (fMaxReturn !== "") out.push({ k: "Max Return", v: fMaxReturn });
    return out;
  }, [fSupplier, fInvoice, fFrom, fTo, fMinReturn, fMaxReturn]);

  const clearFilters = () => {
    setFSupplier("");
    setFInvoice("");
    setFFrom("");
    setFTo("");
    setFMinReturn("");
    setFMaxReturn("");
  };

  // ----- downloads (buttons) -----
  const exportCSV = () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, i) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r, i)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "PurchaseReturnList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  const exportXLSX = () => {
    const cols = visibleCols;
    const data = filteredRows.map((r, i) => {
      const o = {};
      cols.forEach((c) => (o[c.header] = toStr(c.get(r, i))));
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "PurchaseReturnList.xlsx");
  };

  const exportPDF = () => {
    const cols = visibleCols;
    const head = [cols.map((c) => c.header)];
    const body = filteredRows.map((r, i) =>
      cols.map((c) => toStr(c.get(r, i))),
    );

    const doc = new jsPDF("landscape", "mm", "a4");
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(14);
    doc.text("Purchase Return List", 14, 14);
    doc.autoTable({
      startY: 20,
      head,
      body,
      styles: { fontSize: 8, cellPadding: 2, halign: "center" },
      headStyles: { fillColor: [244, 247, 246], textColor: [15, 23, 42] },
    });
    doc.save("PurchaseReturnList.pdf");
  };

  // ----- column chooser modal logic -----
  const openCols = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModalCols(true);
  };
  const closeCols = () => setModalCols(false);

  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = () => {
    localStorage.setItem("TargetList", JSON.stringify(selectedKeys));
    closeCols();
  };

  // ----- SuperAdmin switch -----
  const handleParentSubmit = async () => {
    const a = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    if (!a) return;
    const [id, db] = String(a).split(" ");
    await reload(id, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // ----- loading -----
  if (loading && !rows.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Toolbar */}
      <Row className="pr-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="pr-card-light">
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div className="pr-title">
                Purchase Return List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
                <span
                  style={{ fontWeight: 800, color: "#64748b", fontSize: 12 }}
                >
                  (Total: {rows.length})
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow && (
                <div
                  style={{
                    minWidth: 320,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              )}

              <div className="pr-actionsbar">
                <div className="pr-search">
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff" }}
                  />
                </div>

                <Button
                  className="pr-btn-outline"
                  outline
                  color="primary"
                  onClick={() => setShowFilters((s) => !s)}
                  title="Show/Hide Filters"
                >
                  <FaFilter size={14} /> Filters
                </Button>

                {permissions?.View && (
                  <Button
                    className="pr-btn-outline"
                    outline
                    color="primary"
                    onClick={openCols}
                    title="Choose Columns"
                  >
                    <FaFilter size={14} /> Columns
                  </Button>
                )}

                {permissions?.Download && (
                  <>
                    <Button
                      className="pr-btn"
                      color="primary"
                      onClick={exportXLSX}
                      title="Download Excel"
                    >
                      <ImDownload /> Excel
                    </Button>
                    <Button
                      className="pr-btn-outline"
                      outline
                      color="primary"
                      onClick={exportPDF}
                      title="Download PDF"
                    >
                      <ImDownload /> PDF
                    </Button>
                    <Button
                      className="pr-btn-outline"
                      outline
                      color="primary"
                      onClick={exportCSV}
                      title="Download CSV"
                    >
                      <ImDownload /> CSV
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="pr-filterbar">
                <div className="pr-filter-row">
                  <div className="pr-fitem">
                    <label>Supplier (contains)</label>
                    <Input
                      value={fSupplier}
                      onChange={(e) => setFSupplier(e.target.value)}
                      placeholder="Type supplier..."
                    />
                  </div>

                  <div className="pr-fitem">
                    <label>Invoice No (contains)</label>
                    <Input
                      value={fInvoice}
                      onChange={(e) => setFInvoice(e.target.value)}
                      placeholder="Type invoice..."
                    />
                  </div>

                  <div className="pr-fitem">
                    <label>Date From</label>
                    <Input
                      type="date"
                      value={fFrom}
                      onChange={(e) => setFFrom(e.target.value)}
                    />
                  </div>

                  <div className="pr-fitem">
                    <label>Date To</label>
                    <Input
                      type="date"
                      value={fTo}
                      onChange={(e) => setFTo(e.target.value)}
                    />
                  </div>

                  <div className="pr-fitem">
                    <label>Min Return Amount</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMinReturn}
                      onChange={(e) => setFMinReturn(e.target.value)}
                    />
                  </div>

                  <div className="pr-fitem">
                    <label>Max Return Amount</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMaxReturn}
                      onChange={(e) => setFMaxReturn(e.target.value)}
                    />
                  </div>

                  <div className="pr-fitem" style={{ minWidth: 220 }}>
                    <label>Actions</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        className="pr-btn-outline"
                        outline
                        color="secondary"
                        onClick={clearFilters}
                      >
                        Clear
                      </Button>
                      <Button
                        className="pr-btn-outline"
                        outline
                        color="primary"
                        onClick={() => {
                          const u = JSON.parse(
                            localStorage.getItem("userData") || "{}",
                          );
                          reload(u?._id, u?.database);
                        }}
                        title="Refresh"
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                </div>

                {!!chips.length && (
                  <div className="pr-chipline">
                    {chips.map((c, i) => (
                      <span className="pr-chip" key={`${c.k}-${i}`}>
                        {c.k}: {c.v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="pr-card" style={{ padding: 8 }}>
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No purchase returns found.
              </div>
            ) : (
              <div className="pr-table-wrap">
                <table className="pr-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr key={r?._id || i}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, i)
                              : toStr(c.get(r, i))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modalCols}
        toggle={closeCols}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="pick-box">
                {baseColumns
                  .filter((c) => c.key !== "_actions_")
                  .map((c) => (
                    <div
                      key={c.key}
                      className="pick-item"
                      style={{ cursor: "default" }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          margin: 0,
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleCheckbox(e.target.checked, c.key)
                          }
                        />
                        <span>{c.header}</span>
                      </label>
                    </div>
                  ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="pick-box" style={{ flex: 1 }}>
                  {selectedKeys.map((k, i) => {
                    const col = colMap.get(k);
                    if (!col) return null;
                    return (
                      <div
                        key={k}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        className="modal-dialog modal-xl"
        size="lg"
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          View Details
        </ModalHeader>
        <ModalBody>
          {viewRow ? (
            <>
              <Row className="mb-2">
                <Col lg="3" md="3">
                  <div>Return Amount:</div>
                  <h5>{toNum(viewRow?.Return_amount).toFixed(2)} Rs/-</h5>
                </Col>
                <Col lg="3" md="3">
                  <div>Supplier Name:</div>
                  <h5>{viewRow?.partyId?.CompanyName || "-"}</h5>
                </Col>
                <Col lg="3" md="3">
                  <div>Address:</div>
                  <h5>{viewRow?.partyId?.address || "-"}</h5>
                </Col>
                <Col>
                  <div>Amount:</div>
                  <h5>
                    <strong>{toNum(viewRow?.amount).toFixed(2)}</strong> Rs/-
                  </h5>
                </Col>

                {viewRow?.igstTaxType === 1 ? (
                  <Col>
                    <div>IGST:</div>
                    <h5>
                      <strong>{toNum(viewRow?.igstTotal).toFixed(2)}</strong>{" "}
                      Rs/-
                    </h5>
                  </Col>
                ) : (
                  <>
                    <Col>
                      <div>SGST:</div>
                      <h5>
                        <strong>{toNum(viewRow?.sgstTotal).toFixed(2)}</strong>{" "}
                        Rs/-
                      </h5>
                    </Col>
                    <Col>
                      <div>CGST:</div>
                      <h5>
                        <strong>{toNum(viewRow?.cgstTotal).toFixed(2)}</strong>{" "}
                        Rs/-
                      </h5>
                    </Col>
                  </>
                )}

                <Col>
                  <div>Return Total:</div>
                  <h5>
                    <strong>{toNum(viewRow?.Return_amount).toFixed(2)}</strong>{" "}
                    Rs/-
                  </h5>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="pr-table-wrap">
                    <Table responsive style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Product Name</th>
                          <th>HSN CODE</th>
                          <th>Price</th>
                          <th>Quantity</th>
                          <th>Taxable</th>
                          {viewRow?.igstTaxType === 1 ? (
                            <th>IGST</th>
                          ) : (
                            <>
                              <th>SGST</th>
                              <th>CGST</th>
                            </>
                          )}
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewRow?.returnItems || []).map((it, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{it?.productId?.Product_Title}</td>
                            <td>{it?.productId?.HSN_Code}</td>
                            <td>{toNum(it?.price).toFixed(2)}</td>
                            <td>{toNum(it?.qtyReturn)}</td>
                            <td>{toNum(it?.taxableAmount).toFixed(2)}</td>
                            {viewRow?.igstTaxType === 1 ? (
                              <td>{toNum(it?.igstRate).toFixed(2)}</td>
                            ) : (
                              <>
                                <td>{toNum(it?.sgstRate).toFixed(2)}</td>
                                <td>{toNum(it?.cgstRate).toFixed(2)}</td>
                              </>
                            )}
                            <td>{toNum(it?.grandTotal).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <div>No record.</div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
